// External Libraries
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import type { ReactNode } from "react";

import type { FormElement, FormElements } from "../../helpers/register";

const ROLE_OPTIONS = [
  { name: "Backend Developer", value: "Backend Developer" },
  { name: "Frontend Developer", value: "Frontend Developer" },
  { name: "Mobile Developer", value: "Mobile Developer" },
  { name: "Native Developer", value: "Native Developer" },
  { name: "Cloud Engineer", value: "Cloud Engineer" },
  { name: "Data Scientist", value: "Data Scientist" },
  { name: "DevSecOps", value: "DevSecOps" },
  { name: "Privacy Engineer", value: "Privacy Engineer" },
  { name: "Tester", value: "Tester" },
  { name: "Business Learner", value: "Business Learner" },
  { name: "Other", value: "Other" },
];

interface RegistrationField {
  name: string;
  label: string;
  placeholder: string;
  type?: string;
  id?: string;
}

const REGISTRATION_FIELDS: RegistrationField[] = [
  { name: "email", label: "Email", placeholder: "Enter your email address", type: "email" },
  {
    name: "password",
    label: "Password",
    placeholder: "Create a strong password",
    type: "password",
  },
  { name: "givenName", label: "Given Name", placeholder: "Your given name" },
  { name: "familyName", label: "Family Name", placeholder: "Your family name" },
  { name: "company", label: "Company", placeholder: "Your company" },
  { name: "title", id: "jobTitle", label: "Job Title", placeholder: "Your job title" },
  {
    name: "jobRole",
    label: "Job Role",
    placeholder: "Select the option that best describes your job role",
    type: "dropdown",
  },
];

interface ElementForDisplay {
  status: string;
  message?: string;
  details?: string[];
}

export function ValidationMessage({ element, id }: { element: ElementForDisplay; id: string }) {
  if (element.status !== "invalid" || !element.message) return null;

  return (
    <small className="p-error" id={id} role="alert">
      {element.message}
      {element.details && (
        <ul className="mt-1 ml-4 list-disc">
          {element.details.map((detail, i) => (
            <li key={i}>{detail}</li>
          ))}
        </ul>
      )}
    </small>
  );
}

export function FormField({
  id,
  label,
  element,
  children,
}: {
  id: string;
  label: string;
  element: ElementForDisplay;
  children: ReactNode;
}) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      {children}
      <ValidationMessage element={element} id={errorId} />
    </div>
  );
}

export function RegistrationInput({
  field,
  element,
  onUpdate,
}: {
  field: RegistrationField;
  element: FormElement;
  onUpdate: (name: string, value: string) => void;
}) {
  const id = field.id ?? field.name;
  const isInvalid = element.status === "invalid";
  const errorId = `${id}-error`;
  const a11yProps = {
    "aria-label": field.placeholder,
    "aria-required": "true" as const,
    "aria-invalid": isInvalid,
    ...(isInvalid ? { "aria-describedby": errorId } : {}),
  };

  if (field.type === "password") {
    return (
      <Password
        placeholder={field.placeholder}
        inputId={id}
        data-testid={`demo-registration-${id}-input`}
        value={element.value}
        invalid={isInvalid}
        onChange={e => onUpdate(field.name, e.target.value)}
        toggleMask
        feedback={false}
        pt={{
          hideIcon: { className: "absolute right-3" },
          showIcon: { className: "absolute right-3" },
        }}
        {...a11yProps}
      />
    );
  }

  if (field.type === "dropdown") {
    return (
      <Dropdown
        placeholder={field.placeholder}
        inputId={id}
        data-testid={`demo-registration-${id}-input`}
        options={ROLE_OPTIONS}
        optionLabel="name"
        optionValue="value"
        value={element.value}
        invalid={isInvalid}
        onChange={e => onUpdate(field.name, e.value)}
        pt={{ select: { "aria-label": field.placeholder } }}
        {...a11yProps}
      />
    );
  }

  return (
    <InputText
      placeholder={field.placeholder}
      id={id}
      data-testid={`demo-registration-${id}-input`}
      type={field.type ?? "text"}
      value={element.value}
      invalid={isInvalid}
      onChange={e => onUpdate(field.name, e.target.value)}
      {...a11yProps}
    />
  );
}

interface RegistrationFormProps {
  formElements: FormElements;
  onUpdate: (name: string, value: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
}

export function RegistrationForm({
  formElements,
  onUpdate,
  onSubmit,
  isSubmitting,
}: RegistrationFormProps) {
  return (
    <form
      className="flex flex-col gap-4 p-fluid"
      onSubmit={onSubmit}
      data-testid="demo-registration-form"
    >
      {REGISTRATION_FIELDS.map(field => {
        const id = field.id ?? field.name;
        const element = formElements[field.name];

        return (
          <FormField key={field.name} id={id} label={field.label} element={element}>
            <RegistrationInput field={field} element={element} onUpdate={onUpdate} />
          </FormField>
        );
      })}
      <Button
        type="submit"
        data-testid="demo-registration-submit-button"
        label="Create account"
        loading={isSubmitting}
        disabled={isSubmitting}
      />
    </form>
  );
}

interface ConfirmationFormProps {
  confirmationCode: string;
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onResend: () => void;
  isVerifying: boolean;
  isResendingCode: boolean;
}

export function ConfirmationForm({
  confirmationCode,
  onCodeChange,
  onSubmit,
  onResend,
  isVerifying,
  isResendingCode,
}: ConfirmationFormProps) {
  return (
    <form
      className="flex flex-col gap-4 p-fluid"
      onSubmit={onSubmit}
      data-testid="demo-registration-confirmation-form"
    >
      <Message
        className="w-full max-w-md mx-auto"
        severity="secondary"
        text="A confirmation code has been sent to your email. Please enter it below."
      />
      <FormField id="confirmationCode" label="Confirmation Code" element={{ status: "empty" }}>
        <InputText
          placeholder="Enter confirmation code"
          id="confirmationCode"
          data-testid="demo-registration-confirmation-code-input"
          type="text"
          value={confirmationCode}
          onChange={onCodeChange}
          autoFocus
        />
      </FormField>
      <Button
        type="submit"
        data-testid="demo-registration-confirmation-submit-button"
        label="Submit confirmation code"
        loading={isVerifying}
        disabled={isVerifying}
      />
      <Button
        type="button"
        data-testid="demo-registration-resend-button"
        label="Resend confirmation code"
        severity="secondary"
        onClick={onResend}
        loading={isResendingCode}
        disabled={isResendingCode}
      />
    </form>
  );
}
